import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Plus, X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { foldText, matchesQuery } from '../../utils/search';

export interface SearchSelectOption {
  value: string;
  label: string;
  // Second line, e.g. a class's campus and schedule.
  description?: string;
  // Consecutive options with the same group get one heading.
  group?: string;
  // Extra text that matches the search but is not shown.
  keywords?: string;
  disabled?: boolean;
  // Shown greyed out but still selectable, e.g. an inactive class.
  muted?: boolean;
}

export interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  label?: string;
  'aria-label'?: string;
  id?: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  // A first choice meaning "nothing picked" (value ''), e.g. "Tất cả lớp". Its label is the placeholder.
  emptyOption?: string;
  // Lets the field be cleared back to '' even without an emptyOption.
  clearable?: boolean;
  // Free text: the value is the typed text, matched to an existing option ignoring case and accents.
  creatable?: { label?: (text: string) => string };
  noResultsText?: string;
}

const GAP = 4;
const MAX_SHOWN = 200;

type Item =
  | { kind: 'option'; option: SearchSelectOption }
  | { kind: 'empty'; label: string }
  | { kind: 'create'; text: string };

// A select you can type into to filter, for lists too long to scroll (classes, staff, students).
export function SearchSelect({
  value,
  onChange,
  options,
  label,
  'aria-label': ariaLabel,
  id,
  placeholder,
  hint,
  error,
  disabled,
  className,
  emptyOption,
  clearable,
  creatable,
  noResultsText = 'Không tìm thấy',
}: SearchSelectProps) {
  const reactId = React.useId();
  const inputId = id ?? reactId;
  const listId = `${inputId}-list`;
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  // null while not typing: the field shows the selected option.
  const [query, setQuery] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [position, setPosition] = React.useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const selected = options.find(option => option.value === value) ?? null;
  const displayText = creatable ? value : selected?.label ?? '';
  const typed = query ?? '';

  const exactMatch = (text: string) => {
    const folded = foldText(text);
    return folded ? options.find(option => foldText(option.label) === folded) ?? null : null;
  };

  const matches = React.useMemo(
    () => options.filter(option => matchesQuery(
      [option.label, option.description, option.group, option.keywords].filter(Boolean).join(' '),
      typed,
    )),
    [options, typed],
  );

  const items: Item[] = [];
  if (emptyOption !== undefined && !typed.trim()) items.push({ kind: 'empty', label: emptyOption });
  matches.slice(0, MAX_SHOWN).forEach(option => items.push({ kind: 'option', option }));
  if (creatable && typed.trim() && !exactMatch(typed)) items.push({ kind: 'create', text: typed.trim() });

  const selectable = (item: Item | undefined) => Boolean(item) && !(item!.kind === 'option' && item!.option.disabled);

  const place = React.useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const spaceBelow = window.innerHeight - rect.bottom - GAP - 8;
    const spaceAbove = rect.top - GAP - 8;
    const wanted = Math.min(listRef.current?.scrollHeight ?? 288, 288);
    if (spaceBelow >= wanted || spaceBelow >= spaceAbove) {
      setPosition({ top: rect.bottom + GAP, left, width, maxHeight: Math.max(120, Math.min(288, spaceBelow)) });
    } else {
      const maxHeight = Math.max(120, Math.min(288, spaceAbove));
      setPosition({ top: rect.top - GAP - Math.min(wanted, maxHeight), left, width, maxHeight });
    }
  }, []);

  React.useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place, items.length]);

  // Start on the selected option, or the first one that can be picked.
  React.useEffect(() => {
    if (!open) return;
    const selectedIndex = query === null
      ? items.findIndex(item => (item.kind === 'option' && item.option.value === value) || (item.kind === 'empty' && !value))
      : -1;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : Math.max(0, items.findIndex(selectable)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, typed]);

  React.useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const commitTyped = () => {
    if (!creatable || query === null) return;
    const text = query.trim();
    const next = text ? exactMatch(text)?.value ?? text : '';
    if (next !== value) onChange(next);
  };

  const choose = (item: Item) => {
    if (!selectable(item)) return;
    const next = item.kind === 'option' ? item.option.value : item.kind === 'create' ? item.text : '';
    if (next !== value) onChange(next);
    close();
  };

  const move = (step: number) => {
    if (items.length === 0) return;
    let index = activeIndex;
    for (let count = 0; count < items.length; count += 1) {
      index = (index + step + items.length) % items.length;
      if (selectable(items[index])) break;
    }
    setActiveIndex(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      else move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter') {
      if (open && items[activeIndex]) {
        event.preventDefault();
        choose(items[activeIndex]);
      } else if (creatable && query !== null) {
        event.preventDefault();
        commitTyped();
        close();
      }
    } else if (event.key === 'Escape' && open) {
      // Close the list only, not a modal around it.
      event.stopPropagation();
      close();
    }
  };

  const canClear = !disabled && Boolean(value) && (emptyOption !== undefined || clearable || Boolean(creatable));
  const describedBy = error || hint ? `${inputId}-hint` : undefined;
  let lastGroup: string | undefined;

  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={inputId} className="block text-sm font-medium text-ink-700">
          {label}
        </label>
      ) : null}
      <div ref={anchorRef} className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-label={label ? undefined : ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && items[activeIndex] ? `${listId}-${activeIndex}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          placeholder={emptyOption ?? placeholder}
          value={query ?? displayText}
          onFocus={event => {
            event.target.select();
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onBlur={() => {
            commitTyped();
            close();
          }}
          onChange={event => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'block min-h-10 w-full rounded-lg border bg-white pl-3 text-sm text-ink-900 transition-colors duration-200 placeholder:text-ink-500',
            'focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-500',
            canClear ? 'pr-14' : 'pr-9',
            selected?.muted && query === null ? 'text-ink-500' : null,
            error
              ? 'border-danger-100 focus:border-danger-600 focus:ring-danger-100'
              : 'border-ink-300 focus:border-academy-500 focus:ring-academy-100',
            className,
          )}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1 text-ink-400">
          {canClear ? (
            <button
              type="button"
              tabIndex={-1}
              aria-label={emptyOption ? `Bỏ chọn, ${emptyOption.toLowerCase()}` : 'Bỏ chọn'}
              className="pointer-events-auto rounded p-0.5 hover:bg-ink-100 hover:text-ink-700"
              onMouseDown={event => event.preventDefault()}
              onClick={event => {
                event.stopPropagation();
                onChange('');
                close();
              }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <ChevronDown className="h-4 w-4" />
        </div>
      </div>
      {error ? (
        <p id={describedBy} className="text-sm text-danger-600">{error}</p>
      ) : hint ? (
        <p id={describedBy} className="text-sm text-ink-500">{hint}</p>
      ) : null}
      {open
        ? createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label ?? ariaLabel}
            // Keep focus in the field so blur still means "done".
            onMouseDown={event => event.preventDefault()}
            onClick={event => event.stopPropagation()}
            style={{
              position: 'fixed',
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: position?.width ?? 240,
              maxHeight: position?.maxHeight ?? 288,
            }}
            className="z-[80] overflow-y-auto rounded-lg border border-ink-200 bg-white py-1 text-sm shadow-raised"
          >
            {items.length === 0 ? (
              <p className="px-3 py-2 text-ink-500">{noResultsText}</p>
            ) : items.map((item, index) => {
              const group = item.kind === 'option' ? item.option.group : undefined;
              const heading = group && group !== lastGroup ? group : null;
              lastGroup = item.kind === 'option' ? group : lastGroup;
              const isSelected = item.kind === 'option' ? item.option.value === value : item.kind === 'empty' ? !value : false;
              const isDisabled = !selectable(item);
              return (
                <React.Fragment key={item.kind === 'option' ? `o-${item.option.value}` : item.kind}>
                  {heading ? (
                    <p className="px-3 pb-1 pt-2 text-xs font-semibold text-ink-500" role="presentation">{heading}</p>
                  ) : null}
                  <div
                    id={`${listId}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled || undefined}
                    onMouseEnter={() => !isDisabled && setActiveIndex(index)}
                    onClick={() => choose(item)}
                    className={cn(
                      'flex cursor-pointer items-start gap-2 px-3 py-2',
                      index === activeIndex && !isDisabled ? 'bg-academy-50' : null,
                      isDisabled ? 'cursor-not-allowed opacity-50' : null,
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      {item.kind === 'create' ? (
                        <span className="flex items-center gap-1.5 font-medium text-academy-700">
                          <Plus className="h-4 w-4 shrink-0" />
                          {creatable?.label ? creatable.label(item.text) : `Thêm "${item.text}"`}
                        </span>
                      ) : item.kind === 'empty' ? (
                        <span className="text-ink-600">{item.label}</span>
                      ) : (
                        <>
                          <span className={cn('block truncate', item.option.muted ? 'text-ink-500' : 'text-ink-900')}>{item.option.label}</span>
                          {item.option.description ? (
                            <span className="block truncate text-xs text-ink-500">{item.option.description}</span>
                          ) : null}
                        </>
                      )}
                    </span>
                    {isSelected ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-academy-600" /> : null}
                  </div>
                </React.Fragment>
              );
            })}
            {matches.length > MAX_SHOWN ? (
              <p className="px-3 py-2 text-xs text-ink-500">Còn {matches.length - MAX_SHOWN} kết quả, gõ thêm để thu hẹp.</p>
            ) : null}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
